import React, { useContext, useEffect, useState } from 'react';
import useForm from '../useForm';
import { getFormStats } from '../../../../services/forms/formStatsService';
import { getFormByYear } from '../../../../services/forms/formsService';
import { ExamYearContext } from '../../../../utils/contexts/ExamYearContext';
import ContentList from './ContentList';
import Select from '../../../../components/forms/Select/Select';
import { getSchools } from '../../../../services/catalog/catalog';
import { UserContext } from '../../../../utils/contexts/UserContext';
import SimpleLoader from '../../../../components/Loader/SimpleLoader';
import { Button } from '../../../../components/forms/Buttons/Button';

export default function FormStats() {
  const { user } = useContext(UserContext);
  const { examYear, yearsLoaded } = useContext(ExamYearContext);
  const examYearUUID = examYear?.uuid;
  // Опрос — своя форма на каждый год экзамена. survey = { year, uuid, failed },
  // где year — год, к которому относится ответ by_year. Пока он не совпал с
  // выбранным годом, форма неизвестна: ни форму, ни статистику не запрашиваем.
  const [survey, setSurvey] = useState(null);
  const [surveyAttempt, setSurveyAttempt] = useState(0);
  const surveyResolved = yearsLoaded && survey?.year === examYearUUID;
  const formUUID = surveyResolved && !survey.failed ? survey.uuid : undefined;
  const { form, contents } = useForm(formUUID);
  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolLoading, setSchoolLoading] = useState(false);

  useEffect(() => {
    setSchoolLoading(true);
    if (user?.roles?.some((r) => r.code === 'VP')) {
      setSchools([user.school]);
      setSchool(user.school?.uuid);
      setSchoolLoading(false);
    } else {
      getSchools({ exam_year: examYear?.uuid })
        ?.then((res) => {
          setSchools(res.results);
        })
        .finally(() => setSchoolLoading(false));
    }
  }, [examYear?.uuid, user?.roles, user.school]);

  useEffect(() => {
    // до загрузки списка годов examYear — заглушка с несуществующим uuid
    if (!yearsLoaded || !examYearUUID) return;

    const controller = new AbortController();

    getFormByYear({ exam_year: examYearUUID, signal: controller.signal })
      .then((res) => {
        setSurvey({ year: examYearUUID, uuid: res?.uuid ?? null });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Ошибка при получении формы опроса:', err);
        setSurvey({ year: examYearUUID, uuid: null, failed: true });
      });

    return () => {
      controller.abort();
    };
  }, [examYearUUID, yearsLoaded, surveyAttempt]);

  useEffect(() => {
    if (!formUUID) return;

    const controller = new AbortController();

    setLoading(true);
    getFormStats({
      params: {
        form: formUUID,
        exam_year: examYearUUID,
        school,
      },
      signal: controller.signal,
    })
      .then((res) => {
        setStats(res);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Ошибка при получении статистики опроса:', err);
      })
      .finally(() => {
        // отменённый запрос не должен гасить лоадер следующего
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [examYearUUID, formUUID, school]);

  const surveyPlaceholder = !surveyResolved ? (
    <SimpleLoader className='h-10' />
  ) : survey.failed ? (
    <div className='flex flex-col items-center gap-4 rounded-lg bg-white p-6'>
      Не удалось загрузить форму опроса
      <Button
        text={'Повторить'}
        onClick={() => {
          setSurvey(null);
          setSurveyAttempt((attempt) => attempt + 1);
        }}
      />
    </div>
  ) : !survey.uuid ? (
    <div className='rounded-lg bg-white p-6 text-center'>
      Нет формы опроса за {examYear?.year} год
    </div>
  ) : null;

  return (
    <div className='relative mx-auto flex h-full flex-col items-center overflow-y-auto border-t bg-slate-200 p-8 sm:p-12 md:p-16 lg:p-24'>
      <div className='relative flex min-w-full max-w-2xl flex-col gap-4 lg:min-w-[42rem] '>
        {surveyPlaceholder ?? (
          <>
            <div
              className={`flex flex-col gap-2 rounded-lg border-t-[0.6rem]
                  border-t-violet-700 bg-white p-6`}
            >
              <div
                className='text-3xl'
                dangerouslySetInnerHTML={{
                  __html: form?.name,
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: form?.descritption,
                }}
              />
              <Select
                loading={schoolLoading}
                className='w-64'
                label={'Школа'}
                options={schools.map((sc) => ({
                  value: sc.uuid,
                  label: sc.name,
                }))}
                value={school}
                isClearable
                onChange={(v) => setSchool(v?.value)}
              />
            </div>
            <ContentList
              loading={!form || !stats || loading}
              data={{ contents, stats, school, examYear }}
            />
          </>
        )}
      </div>
    </div>
  );
}
